import React, { useEffect, useRef, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { Spinner } from 'react-bootstrap';
import { Check } from 'lucide-react';
import { spreadConfigAPI, ManualSpreadDefault } from '../services/spreadApi';
import { SpreadConfig, SpreadMarket, SpreadDirection, SpreadOptionType, getLegOrder } from '../types/spread';
import { brokerAPI, BrokerStatus, BrokerFeatureToggle } from '../services/brokerApi';

// "Manual Spread" — a quick one-shot trade panel (no saved/named config list to manage).
// Reuses the exact same NY/SX Spread entry/exit engine under the hood via
// spreadConfigAPI.manualPlace/instantExit; this page's only job is the UI + tracking which
// configs it created (name prefix "Manual - ").
//
// Layout (client's explicit ask, UI-only — no functional change underneath): CE and PE are shown
// side by side at all times under each BULLISH/BEARISH row, instead of one panel with a CE/PE
// toggle that hides the other. Each side is otherwise identical to the old single toggled panel —
// same fields, same auto-save, same Default/Clear, same Place/Exit — just with `optionType` fixed
// per side instead of switchable.

const FIRST_LEG_OFFSET_OPTIONS = [-3, -2, -1, 0, 1, 2, 3];
const SECOND_LEG_OFFSET_OPTIONS = [1, 2, 3, 4, 5];

interface PanelState {
  firstLegOffset: number;
  secondLegOffset: number;
  // Client's explicit ask: replaces the old flat Lots field — quantity is now sized from this %
  // of the configured Capital Amount (Broker Management) against the SELL leg's LTP, same
  // formula Nifty Buying already uses.
  capitalPercentage: number | '';
  slippagePercentage: number | '';
  delaySeconds: number | '';
  slOffset: number | '';
  slPoints: number | '';
  tslNpPoints: number | '';
  thirdLegEnabled: boolean;
  broker: string;
}

const defaultPanelState = (): PanelState => ({
  firstLegOffset: 1, secondLegOffset: 1,
  capitalPercentage: 1, slippagePercentage: 3, delaySeconds: 3,
  slOffset: '', slPoints: '', tslNpPoints: '', thirdLegEnabled: false, broker: 'DHAN',
});

// A saved default (if one exists for this market/direction/option_type) replaces every field
// above — including SL/TSL — when the panel mounts (confirmed live: the client explicitly wants
// SL/TSL to round-trip through Default too, not just offsets/lots). `handlePlace` below still
// clears SL/TSL right after a successful placement so the *current* session's form never silently
// reuses a just-fired SL/TSL on the very next Place click — the saved default only comes back the
// next time this panel mounts or Default is clicked.
const applyDefault = (base: PanelState, d: ManualSpreadDefault | undefined): PanelState => {
  if (!d) return base;
  return {
    ...base,
    firstLegOffset: d.first_leg_offset, secondLegOffset: d.second_leg_offset, capitalPercentage: d.capital_percentage,
    slippagePercentage: d.slippage_percentage, delaySeconds: d.delay_seconds, broker: d.broker,
    slOffset: d.sl_offset ?? '', slPoints: d.sl_points ?? '', tslNpPoints: d.tsl_np_points ?? '',
    thirdLegEnabled: !!d.third_leg_enabled,
  };
};

const MARKETS: { market: SpreadMarket; label: string; accent: string }[] = [
  { market: 'NIFTY', label: 'NY', accent: '#0d6efd' },
  { market: 'SENSEX', label: 'SX', accent: '#fd7e14' },
];
const DIRECTIONS: { direction: SpreadDirection; label: string; accent: string; bg: string }[] = [
  { direction: 'BULLISH', label: 'BULLISH', accent: '#065f46', bg: '#ecfdf5' },
  { direction: 'BEARISH', label: 'BEARISH', accent: '#991b1b', bg: '#fef2f2' },
];
const OPTION_TYPES: SpreadOptionType[] = ['CE', 'PE'];

const labelStyle: React.CSSProperties = { display: 'block', fontSize: '10px', fontWeight: 500, color: '#374151', marginBottom: '3px' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '4px 5px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' };

const OptionCell: React.FC<{
  market: SpreadMarket; direction: SpreadDirection; optionType: SpreadOptionType; accent: string; bg: string;
  configs: SpreadConfig[]; onPlaced: () => void;
}> = ({ market, direction, optionType, accent, bg, configs, onPlaced }) => {
  const [form, setForm] = useState<PanelState>(defaultPanelState());
  const [placing, setPlacing] = useState(false);
  // Guards against a fast double-click/double-tap firing handlePlace twice before React has
  // re-rendered the button's disabled={placing} — confirmed live as a real cause of duplicate
  // orders, since `placing` state alone only blocks a *second render's* click, not one that fires
  // before that render lands. A ref is synchronous and shared across calls, so it closes that gap
  // regardless of render timing (the backend also locks per-panel as defense in depth).
  const placingRef = useRef(false);
  const [exitingId, setExitingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [brokers, setBrokers] = useState<BrokerStatus[]>([]);
  const [featureToggles, setFeatureToggles] = useState<BrokerFeatureToggle[]>([]);
  const [savedDefault, setSavedDefault] = useState<ManualSpreadDefault | undefined>(undefined);
  const [defaultSaved, setDefaultSaved] = useState(false);
  // Guards the auto-save effect below from firing right after a *programmatic* form change
  // (initial mount, the server fetch resolving, or Clear) — it should only persist changes the
  // user actually typed, never silently re-save whatever a load/reset just put in the fields.
  // Starts true so the very first render (hardcoded defaults) isn't saved.
  const skipNextAutoSaveRef = useRef(true);
  // The auto-save effect only "consumes" (resets) the flag when it actually runs, which React
  // skips if none of its listed dependencies changed value — true whenever a programmatic reset
  // lands on fields that happen to already match what's already there. Without this timeout
  // fallback the flag would stay stuck "true" and silently swallow the user's very next real edit.
  const suppressAutoSave = () => {
    skipNextAutoSaveRef.current = true;
    setTimeout(() => { skipNextAutoSaveRef.current = false; }, 0);
  };
  const isBrokerEnabledFor = (broker: string) =>
    featureToggles.find(t => t.broker === broker && t.feature === 'MANUAL_SPREAD')?.is_enabled ?? (broker === 'DHAN');

  useEffect(() => {
    brokerAPI.list().then(res => setBrokers(res.data.filter(b => b.implemented))).catch(() => {});
    brokerAPI.listFeatures().then(res => setFeatureToggles(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    spreadConfigAPI.getManualDefaults(market).then(res => {
      const mine = res.data.find(d => d.direction === direction && d.option_type === optionType);
      setSavedDefault(mine);
      suppressAutoSave();
      setForm(prev => applyDefault(prev, mine));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market, direction, optionType]);

  // Client's explicit ask: no separate Save button — editing a field auto-saves it (debounced)
  // as this option type's running default, so "the last values you typed" is always what's saved.
  // Skipped right after any *programmatic* form change via skipNextAutoSaveRef (see above), and
  // skipped outright if the required fields are blank (same guard Place itself relies on).
  useEffect(() => {
    if (skipNextAutoSaveRef.current) { skipNextAutoSaveRef.current = false; return; }
    const { capitalPercentage, slippagePercentage, delaySeconds } = form;
    if (capitalPercentage === '' || slippagePercentage === '' || delaySeconds === '') return;
    const timer = setTimeout(() => {
      const payload = {
        direction, option_type: optionType,
        first_leg_offset: form.firstLegOffset, second_leg_offset: form.secondLegOffset,
        capital_percentage: capitalPercentage, slippage_percentage: slippagePercentage, delay_seconds: delaySeconds,
        sl_offset: form.slOffset === '' ? null : form.slOffset,
        sl_points: form.slPoints === '' ? null : form.slPoints,
        tsl_np_points: form.tslNpPoints === '' ? null : form.tslNpPoints,
        third_leg_enabled: form.thirdLegEnabled,
        broker: form.broker,
      };
      spreadConfigAPI.saveManualDefault(market, payload).then(() => {
        setSavedDefault(payload);
        setDefaultSaved(true);
        setTimeout(() => setDefaultSaved(false), 1500);
      }).catch((e: any) => setError(e.response?.data?.error || e.message));
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.firstLegOffset, form.secondLegOffset, form.capitalPercentage, form.slippagePercentage, form.delaySeconds, form.slOffset, form.slPoints, form.tslNpPoints, form.thirdLegEnabled, form.broker]);

  const set = <K extends keyof PanelState>(field: K, value: PanelState[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  // "Default" — loads this option type's saved default (if any) into the fields. A no-op with a
  // clear message if nothing has been auto-saved yet, rather than silently doing nothing.
  const handleLoadDefault = () => {
    if (!savedDefault) {
      setError(`No saved default for ${optionType} on this panel yet — edit a field to create one.`);
      return;
    }
    suppressAutoSave();
    setForm(prev => applyDefault(prev, savedDefault));
  };

  // "Clear" — UI-only: resets the visible fields to the hardcoded starting values (SL/TSL back
  // to "Off"). Does NOT touch the saved default in the DB — confirmed live: the client wants to
  // blank the panel for a fresh look without losing what's saved, since clicking Default right
  // after still needs to bring the saved values back.
  const handleClearDefault = () => {
    suppressAutoSave();
    setForm(defaultPanelState());
  };

  const legOrder = getLegOrder(direction, optionType);
  const firstLegLabel = legOrder === 'BUY_FIRST' ? 'Buy' : 'Sell';
  const secondLegLabel = legOrder === 'BUY_FIRST' ? 'Sell' : 'Buy';
  const secondLegLetter = legOrder === 'BUY_FIRST' ? 'B' : 'S';
  const secondLegSign = optionType === 'CE' ? '+' : '-';

  const openPositions = configs.filter(c => c.direction === direction && c.option_type === optionType);

  const handlePlace = async () => {
    if (placingRef.current) return;
    placingRef.current = true;
    setPlacing(true); setError(null);
    try {
      await spreadConfigAPI.manualPlace(market, {
        direction, option_type: optionType,
        first_leg_offset: form.firstLegOffset, second_leg_offset: form.secondLegOffset,
        capital_percentage: form.capitalPercentage === '' ? 1 : form.capitalPercentage,
        slippage_percentage: form.slippagePercentage === '' ? 3 : form.slippagePercentage,
        delay_seconds: form.delaySeconds === '' ? 3 : form.delaySeconds,
        sl_offset: form.slOffset === '' ? 0 : form.slOffset,
        sl_points: form.slPoints === '' ? 0 : form.slPoints,
        tsl_np_points: form.tslNpPoints === '' ? 0 : form.tslNpPoints,
        third_leg_enabled: form.thirdLegEnabled,
        broker: form.broker,
      });
      // Confirmed live: a stray SL value typed for one test silently carried over into the next
      // Place click on this same panel (form state persists across placements), so a config the
      // client believed had "no SL" actually inherited a real one from an earlier test and
      // exited exactly as designed once it was hit. Reset SL/TSL back to "Off" after every
      // successful placement so each new trade starts clean unless explicitly set again — this is
      // a *session-local* reset only, so it must not auto-save (which would overwrite the saved
      // default's SL/TSL with blanks after every single Place).
      suppressAutoSave();
      setForm(prev => ({ ...prev, slOffset: '', slPoints: '', tslNpPoints: '' }));
      onPlaced();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      placingRef.current = false;
      setPlacing(false);
    }
  };

  const handleExit = async (id: number) => {
    setExitingId(id);
    try {
      await spreadConfigAPI.instantExit(market, id);
      onPlaced();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setExitingId(null);
    }
  };

  return (
    <div style={{ flex: '0 1 320px', minWidth: '280px', background: bg, border: `1px solid ${accent}33`, borderRadius: '6px', padding: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{
          fontSize: '11px', fontWeight: 700, color: 'white', background: accent,
          padding: '3px 10px', borderRadius: '4px',
        }}>{optionType}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {defaultSaved && (
            <span style={{ fontSize: '9px', fontWeight: 600, color: '#065f46', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
              <Check size={11} /> Saved
            </span>
          )}
          <button
            onClick={handleLoadDefault}
            title={`Load the saved ${optionType} default into this panel's fields`}
            style={{
              padding: '4px 8px', fontSize: '10px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer',
              border: '1px solid #9ca3af', background: 'white', color: '#6b7280',
            }}
          >
            Default
          </button>
          <button
            onClick={handleClearDefault}
            title="Reset this panel's fields (does not touch the saved default)"
            style={{
              padding: '4px 8px', fontSize: '10px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer',
              border: '1px solid #fca5a5', background: 'white', color: '#991b1b',
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '6px' }}>
        <div>
          <label style={labelStyle}>{firstLegLabel} (ATM)</label>
          <select style={inputStyle} value={form.firstLegOffset} onChange={e => set('firstLegOffset', parseInt(e.target.value))}>
            {FIRST_LEG_OFFSET_OPTIONS.map(n => <option key={n} value={n}>{n === 0 ? 'ATM' : `ATM${n > 0 ? '+' : ''}${n}`}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>{secondLegLabel} ({secondLegLetter}±)</label>
          <select style={inputStyle} value={form.secondLegOffset} onChange={e => set('secondLegOffset', parseInt(e.target.value))}>
            {SECOND_LEG_OFFSET_OPTIONS.map(n => <option key={n} value={n}>{secondLegLetter}{secondLegSign}{n}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>% Cap</label>
          <input type="number" min={0.01} step={0.01} style={inputStyle} value={form.capitalPercentage} onChange={e => set('capitalPercentage', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </div>
      </div>

      {/* Same split-hedge feature as NY/SX Spread configs — reuses the exact same entry logic
          (executeSpreadEntryOrder), just not exposed on this panel until now. Splits whichever
          leg sits at the {secondLegLabel} offset into two (that offset + one step further OTM),
          evenly dividing the lots; the other leg keeps its full lot count. */}
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.thirdLegEnabled} onChange={e => set('thirdLegEnabled', e.target.checked)} />
        <span style={{ fontSize: '10px', color: '#374151' }}>
          Split {secondLegLabel} leg into two ({secondLegLetter}{secondLegSign}{form.secondLegOffset} + {secondLegLetter}{secondLegSign}{form.secondLegOffset + 1}, lots split evenly)
        </span>
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', marginBottom: '8px' }}>
        <div>
          <label style={labelStyle}>Slippage (%)</label>
          <input type="number" step="0.01" style={inputStyle} value={form.slippagePercentage} onChange={e => set('slippagePercentage', e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
        <div>
          <label style={labelStyle}>Delay (s)</label>
          <input type="number" min={0} style={inputStyle} value={form.delaySeconds} onChange={e => set('delaySeconds', e.target.value === '' ? '' : parseInt(e.target.value))} />
        </div>
      </div>
      <div style={{ marginBottom: '8px' }}>
        <label style={labelStyle}>Broker</label>
        <select style={inputStyle} value={form.broker} onChange={e => set('broker', e.target.value)}>
          <option value="DHAN">Dhan</option>
          {brokers.filter(b => b.broker !== 'DHAN').map(b => (
            <option key={b.broker} value={b.broker}>{b.broker.replace('_', ' ')}{!isBrokerEnabledFor(b.broker) ? ' (disabled)' : ''}</option>
          ))}
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', marginBottom: '8px' }}>
        <div>
          <label style={labelStyle}>SL ({direction === 'BULLISH' ? 'X' : 'Y'} pts from entry)</label>
          <input type="number" min={0} placeholder="Off" style={inputStyle} value={form.slOffset}
            onChange={e => set('slOffset', e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
        <div>
          <label style={labelStyle}>SL (NP — {market === 'SENSEX' ? 'Sensex' : 'Nifty'} pts)</label>
          <input type="number" min={0} placeholder="Off" style={inputStyle} value={form.slPoints}
            onChange={e => set('slPoints', e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>TSL (NP — {market === 'SENSEX' ? 'Sensex' : 'Nifty'} pts, trail)</label>
          <input type="number" min={0} placeholder="Off" style={inputStyle} value={form.tslNpPoints}
            onChange={e => set('tslNpPoints', e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
      </div>

      <button
        onClick={handlePlace} disabled={placing}
        style={{ width: '100%', padding: '5px', fontSize: '11px', fontWeight: 700, color: 'white', background: placing ? '#9ca3af' : accent, border: 'none', borderRadius: '4px', cursor: placing ? 'default' : 'pointer' }}
      >
        {placing ? <Spinner size="sm" animation="border" /> : `Place (${firstLegLabel} first)`}
      </button>

      {error && <div style={{ fontSize: '9px', color: '#991b1b', marginTop: '6px' }}>{error}</div>}

      {openPositions.length > 0 && (
        <div style={{ marginTop: '10px', borderTop: `1px solid ${accent}33`, paddingTop: '8px' }}>
          {openPositions.map(cfg => (
            <div key={cfg.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '10px', marginBottom: '4px' }}>
              <span>
                {cfg.option_type} · ATM{cfg.first_leg_offset >= 0 ? '+' : ''}{cfg.first_leg_offset} · {secondLegLetter}{secondLegSign}{cfg.second_leg_offset} · {cfg.capital_percentage}%
                {' '}<span style={{ color: cfg.exit_active ? '#065f46' : '#6b7280', fontWeight: 600 }}>{cfg.exit_active ? 'OPEN' : 'ENTERING'}</span>
              </span>
              <button
                onClick={() => handleExit(cfg.id)} disabled={exitingId === cfg.id}
                style={{ padding: '2px 8px', fontSize: '9px', fontWeight: 600, color: '#991b1b', background: 'white', border: '1px solid #fca5a5', borderRadius: '4px', cursor: exitingId === cfg.id ? 'default' : 'pointer' }}
              >
                {exitingId === cfg.id ? '...' : 'Exit'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// A direction "row": a label (BULLISH/BEARISH) above two OptionCells (CE, PE) shown side by side
// at all times — replaces the old single panel with a CE/PE toggle that hid one at a time.
const DirectionRow: React.FC<{
  market: SpreadMarket; direction: SpreadDirection; label: string; accent: string; bg: string;
  configs: SpreadConfig[]; onPlaced: () => void;
}> = ({ market, direction, label, accent, bg, configs, onPlaced }) => {
  const visibleTypes = OPTION_TYPES.filter(ot => !(direction === 'BULLISH' && ot === 'CE'));
  return (
  <div style={{ marginBottom: '14px' }}>
    <div style={{ fontSize: '11px', fontWeight: 700, color: accent, marginBottom: '6px' }}>{label}</div>
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {/* Client's explicit ask: hide the Bullish CE cell specifically (both NIFTY and SENSEX) —
          Bullish PE and both Bearish cells stay as-is. flexWrap lets the cells (each capped at a
          320px max width below, 280px floor on tablet) stack vertically instead of forcing
          horizontal overflow once the market column itself shrinks on tablet. */}
      {visibleTypes.map(ot => (
        <OptionCell
          key={ot} market={market} direction={direction} optionType={ot} accent={accent} bg={bg}
          configs={configs} onPlaced={onPlaced}
        />
      ))}
    </div>
  </div>
  );
};

const ManualSpreadPage: React.FC = () => {
  const [configsByMarket, setConfigsByMarket] = useState<Record<SpreadMarket, SpreadConfig[]>>({ NIFTY: [], SENSEX: [] });

  const fetchAll = async () => {
    try {
      const [niftyRes, sensexRes] = await Promise.all([
        spreadConfigAPI.getConfigs('NIFTY'),
        spreadConfigAPI.getConfigs('SENSEX'),
      ]);
      const isOpenManual = (c: any) => c.name?.startsWith('Manual - ') && (c.is_active || c.exit_active);
      setConfigsByMarket({
        NIFTY: (niftyRes.data as SpreadConfig[]).filter(isOpenManual),
        SENSEX: (sensexRes.data as SpreadConfig[]).filter(isOpenManual),
      });
    } catch (err: any) {
      console.error('[ManualSpread] Failed to fetch configs:', err.message);
    }
  };

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <MainLayout>
      <div style={{ padding: '20px' }}>
        <h5 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 700 }}>Manual Spread</h5>
        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '14px' }}>
          One-shot two-leg spread trade — no saved config, place and exit directly. Needs the market's
          own BULLISH/BEARISH master switch and the N/B kill switch both ON, same as NY/SX Spread.
        </div>

        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {MARKETS.map(({ market, label, accent }) => (
            <div key={market} style={{ flex: '1 1 600px', minWidth: 0 }}>
              <h6 style={{ fontSize: '12px', fontWeight: 700, color: accent, marginBottom: '8px' }}>{label} ({market})</h6>
              {DIRECTIONS.map(({ direction, label: dLabel, accent: dAccent, bg }) => (
                <DirectionRow
                  key={direction} market={market} direction={direction} label={dLabel} accent={dAccent} bg={bg}
                  configs={configsByMarket[market]} onPlaced={fetchAll}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </MainLayout>
  );
};

export default ManualSpreadPage;
